import * as React from 'react';

import { LiveViewRow } from './interfaces';
import { MayNeedHelpTag } from './mayNeedHelp';
import PremiumCallOut, { SchoolAdmin } from './premiumCallOut';
import PromptCircles from './promptCircles';
import { timeSpentAt } from './timeSpent';

import { sortByLastName } from '../../../../modules/sortingMethods';
import { escapeHtml } from '../assignment_flow/manage_activities_v2/shared';
import { ReactTable, Tooltip, helpIcon, safeParseHtml, safeStripHtml } from '../../../Shared/index';

// The sortable columns' ids, as react-table reports them from header clicks
export const SORT_BY_TIME_SPENT = 'timeSpent';
export const SORT_BY_STUDENT_NAME = 'studentName';
export const SORT_BY_ACTIVITY = 'activityName';

export interface LiveViewSort {
  id: typeof SORT_BY_TIME_SPENT | typeof SORT_BY_STUDENT_NAME | typeof SORT_BY_ACTIVITY,
  desc: boolean,
}

export const DEFAULT_SORT: LiveViewSort = { id: SORT_BY_TIME_SPENT, desc: true };

interface LiveViewTableProps {
  rows: LiveViewRow[],
  // School or District Premium: every student in full
  fullAccess: boolean,
  sort?: LiveViewSort,
  onSortChange?: (sort: LiveViewSort) => void,
  maxRows?: number,
  showcaseFirst?: boolean,
  now?: Date,
  emptyMessage?: React.ReactNode,
  emptyAction?: React.ReactNode,
  schoolAdmins?: SchoolAdmin[]|null,
}

// The shared ReactTable fixes its page size at the row count of its first
// render, which silently hides rows that arrive later; these tables have no
// pagination, so every row goes on the one page
export const SHOW_ALL_ROWS_PAGE_SIZE = Number.MAX_SAFE_INTEGER;

// Placeholder shown under the blur; the real value is withheld server-side
export const REDACTED_ACTIVITY_NAME = 'Activity name';

// The same last-name order as the other reports
const compareByLastName = (a: LiveViewRow, b: LiveViewRow): number => sortByLastName(a.studentName, b.studentName);

// Activity names can carry markup; redacted ones are null and sort last
const plainActivityName = (row: LiveViewRow): string => safeStripHtml(row.activityName).trim();

// Longest time spent first, by default. Students online with no activity open
// have no time or activity, so those sorts keep them last in either direction,
// as they do hidden activity names; name ties fall back to last name A-Z.
export const sortRows = (rows: LiveViewRow[], sort: LiveViewSort = DEFAULT_SORT, now: Date = new Date()): LiveViewRow[] => {
  const direction = sort.desc ? -1 : 1;
  const activityNames = sort.id === SORT_BY_ACTIVITY ? new Map(rows.map(row => [row, plainActivityName(row)])) : null;

  return [...rows].sort((a, b) => {
    if (sort.id === SORT_BY_STUDENT_NAME) { return direction * compareByLastName(a, b); }
    if (!!a.onlineOnly !== !!b.onlineOnly) { return a.onlineOnly ? 1 : -1; }

    if (sort.id === SORT_BY_ACTIVITY) {
      const nameA = activityNames.get(a);
      const nameB = activityNames.get(b);
      if (!nameA !== !nameB) { return nameA ? -1 : 1; }
      return direction * nameA.localeCompare(nameB, undefined, { sensitivity: 'base' }) || compareByLastName(a, b);
    }

    return direction * (timeSpentAt(a, now) - timeSpentAt(b, now)) || compareByLastName(a, b);
  });
};

// The one student shown in full without premium: the earliest-started
// session, skipping students who just finished
export const showcaseUid = (rows: LiveViewRow[]): string|null => {
  const withActivity = rows.filter(row => !row.onlineOnly && !row.completed);
  if (!withActivity.length) { return null; }

  const sorted = [...withActivity].sort((a, b) => (
    new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime() ||
    a.activitySessionUid.localeCompare(b.activitySessionUid)
  ));
  return sorted[0].activitySessionUid;
};

// The tool's own active-time total, as of the student's last report, ticked
// forward locally until the next report resyncs it
export const formatTimeSpent = (row: Pick<LiveViewRow, 'timeSpentSeconds'|'timeSpentAsOf'|'idle'|'timeSpentFloor'>, now: Date): string => {
  const totalSeconds = timeSpentAt(row, now);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const TICK_MS = 1000;
// Fire just after the second so every cell has rolled over
const TICK_LAG_MS = 20;

// One shared clock, ticking on each server second, so the whole time column
// changes together
const tickListeners = new Set<() => void>();
let tickTimeout: number|null = null;
let tickOffsetMs = 0;
// Reset to now when the clock starts, so new cells don't show the older `now`
let lastTick = 0;

const scheduleTick = () => {
  const serverMs = Date.now() + tickOffsetMs;
  tickTimeout = window.setTimeout(() => {
    lastTick = Date.now();
    tickListeners.forEach(listener => listener());
    scheduleTick();
  }, TICK_MS - (((serverMs % TICK_MS) + TICK_MS) % TICK_MS) + TICK_LAG_MS);
};

const subscribeToTick = (listener: () => void) => {
  tickListeners.add(listener);
  if (tickTimeout === null) {
    lastTick = Date.now();
    scheduleTick();
  }
  return () => {
    tickListeners.delete(listener);
    if (tickListeners.size) { return; }

    window.clearTimeout(tickTimeout);
    tickTimeout = null;
  };
};

const getLastTick = () => lastTick;
const stayStill = () => () => {};
const noTick = () => 0;

// Ticks every second between the page's 30-second renders; `now` carries the
// server clock offset. The ticking is inessential motion that CSS reduced-motion
// rules cannot reach, so with that preference set it only moves on the page's
// own renders.
export const TimeSpent = ({ row, now }: { row: LiveViewRow, now: Date }) => {
  const offsetMs = React.useMemo(() => now.getTime() - Date.now(), [now]);
  const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  // The installed React types return unknown here
  const tick = React.useSyncExternalStore(reduceMotion ? stayStill : subscribeToTick, reduceMotion ? noTick : getLastTick) as number;

  // Layout effect: the offset must be set before the first tick is scheduled
  React.useLayoutEffect(() => { tickOffsetMs = offsetMs; }, [offsetMs]);

  return <span className="live-view-time-spent">{formatTimeSpent(row, new Date(Math.max(tick + offsetMs, now.getTime())))}</span>;
};

const STUDENT_HEADER_WIDTH = 180;

// label names the dot, description fills the Student header's key, and
// hoverText fills the tooltip, under the student's name when it's cut off.
const WORKING = { className: '', label: 'Online', description: 'Working on an activity', hoverText: 'Student is online and actively working on an activity.' };
const INACTIVE = { className: 'inactive', label: 'Inactive', description: 'Working on an activity, but inactive for 30 seconds or more. Their timer is paused.', hoverText: 'Student is online and working on an activity, but is currently idle.' };
const NOT_IN_ACTIVITY_STATUS = { className: 'open', label: 'Online, not in an activity', description: 'Online, but not in an activity', hoverText: 'The student is online but not currently in an activity.' };

export const STUDENT_STATUS_INTRO = 'Student Statuses';
export const STUDENT_STATUSES = [WORKING, INACTIVE, NOT_IN_ACTIVITY_STATUS];

const statusKeyRow = ({ className, description }: typeof WORKING) => (
  `<tr><td><span aria-hidden="true" class="online-indicator ${className}"></span></td><td>${description}</td></tr>`
);
export const STUDENT_STATUS_DESCRIPTION = `<p>${STUDENT_STATUS_INTRO}</p><table class="student-status-key"><tbody>${STUDENT_STATUSES.map(statusKeyRow).join('')}</tbody></table>`;

const statusTooltip = (studentName: string, { className, hoverText }: typeof WORKING) => (
  `<span class="student-status-tooltip"><b class="student-status-tooltip-name">${escapeHtml(studentName)}</b><span aria-hidden="true" class="online-indicator ${className}"></span><span>${hoverText}</span></span>`
);
export const TIME_SPENT_DESCRIPTION = 'The time the student has spent in their current activity. A student is marked inactive when they haven’t typed, clicked or moved the mouse for 30 seconds, or when they switch to another browser tab. Inactive time isn’t added to their time spent, and the timer starts again as soon as they come back and interact with the activity.';

const studentStatus = (row: Pick<LiveViewRow, 'idle'|'onlineOnly'>) => {
  if (row.onlineOnly) { return NOT_IN_ACTIVITY_STATUS; }
  if (row.idle) { return INACTIVE; }
  return WORKING;
};

// fixedPosition: the table's scroll container would clip the tooltip.
// Not tabbable: the dot's label already gives the status, without a tab stop
// per student.
// The tooltip's copy of the name only shows when the cell cuts the name off,
// checked on each hover since the column's width can change.
export const StudentNameWithStatus = ({ row }: { row: Pick<LiveViewRow, 'studentName'|'idle'|'onlineOnly'> }) => {
  const status = studentStatus(row);
  const { label, className } = status;
  const wrapperRef = React.useRef<HTMLSpanElement>(null);
  const nameRef = React.useRef<HTMLSpanElement>(null);
  const [nameCutOff, setNameCutOff] = React.useState(false);

  React.useEffect(() => {
    const wrapper = wrapperRef.current;
    const checkNameCutOff = () => setNameCutOff(nameRef.current.scrollWidth > nameRef.current.clientWidth);

    wrapper.addEventListener('mouseover', checkNameCutOff);
    return () => wrapper.removeEventListener('mouseover', checkNameCutOff);
  }, []);

  return (
    <span className={`student-name-with-status-wrapper ${nameCutOff ? 'name-cut-off' : ''}`} ref={wrapperRef}>
      <Tooltip
        fixedPosition
        isTabbable={false}
        tooltipText={statusTooltip(row.studentName, status)}
        tooltipTriggerText={(
          <span className="student-name-with-status">
            <span aria-label={label} className={`online-indicator ${className}`} role="img" />
            <span className="student-name" ref={nameRef}>{row.studentName}</span>
          </span>
        )}
        tooltipTriggerTextClass="student-name-with-status-trigger"
      />
    </span>
  );
};

export const NOT_IN_ACTIVITY = <span className="not-in-activity">Not in an Activity</span>;

export const gated = (content: React.ReactNode, blur: boolean) => (blur ? <span aria-hidden className="non-premium-blur" inert>{content}</span> : content);

// Without full access the table is padded with blurred placeholders to at
// least this many rows, and more if the premium call out over it needs them
export const MIN_PREVIEW_ROWS = 7;
const PLACEHOLDER_UID = 'placeholder';

type TableRow = LiveViewRow & { placeholder?: boolean };

const placeholderRows = (count: number): TableRow[] => Array.from({ length: count }, (_, index) => ({
  activitySessionUid: `${PLACEHOLDER_UID}-${index}`, userId: null, studentName: 'Student name', classroomId: null, activityName: null, toolKey: null,
  startedAt: null, lastSeenAt: null, timeSpentSeconds: 0, timeSpentAsOf: null, stage: null, totalPrompts: 10, completedPromptCount: 0,
  optimalPromptCount: 0, prompts: [], redacted: true, placeholder: true,
}));

// Placeholders are decoration under the blur; their rows are hidden from screen readers
const placeholderCell = (content: React.ReactNode) => gated(content, true);

const placeholderRowProps = (row: { original: TableRow }) => (row.original.placeholder ? { 'aria-hidden': true } : {});

function StudentCell({ row }) {
  if (row.original.placeholder) { return placeholderCell(row.original.studentName); }

  return <StudentNameWithStatus row={row.original} />;
}

// Read by the time spent cells, so the columns (and the cells react-table
// mounts from them) do not change identity every time the clock moves
const NowContext = React.createContext<Date>(new Date());

function TimeSpentCell({ row }) {
  const now = React.useContext(NowContext);
  if (row.original.placeholder) { return placeholderCell('0:00'); }

  return row.original.onlineOnly ? '' : <TimeSpent now={now} row={row.original} />;
}

function MayNeedHelpCell({ row }) {
  const now = React.useContext(NowContext);
  return <MayNeedHelpTag now={now} row={row} />;
}

// Header with a help tooltip, as in the Skills Report; fixed position so the
// clipping header cell doesn't cut it off
export const HeaderWithHelp = ({ label, description }: { label: string, description: string }) => (
  <span className="text-and-tooltip-wrapper">
    <span>{label}</span>
    <span className="header-help">
      <Tooltip fixedPosition tooltipText={description} tooltipTriggerText={<img alt={helpIcon.alt} src={helpIcon.src} />} />
    </span>
  </span>
);

const columns = (fullAccess: boolean, showcase: string|null) => {
  // Without full access, one student (the showcase) shows in full; the rest are blurred
  const showsFullData = (row: LiveViewRow) => !row.redacted && (fullAccess || row.activitySessionUid === showcase || !!row.completed);
  const blurred = (row: LiveViewRow) => !row.onlineOnly && !showsFullData(row);

  // Online-only students have no activity, so their activity columns stay empty
  const cell = (render: (row: LiveViewRow) => React.ReactNode) => ({ row }) => {
    if (row.original.onlineOnly) { return ''; }
    if (row.original.placeholder) { return placeholderCell(render(row.original)); }

    return gated(render(row.original), blurred(row.original));
  };
  return [
    {
      Header: <HeaderWithHelp description={STUDENT_STATUS_DESCRIPTION} label="Student" />,
      ariaLabel: 'Student',
      accessor: SORT_BY_STUDENT_NAME,
      resizable: false,
      Cell: StudentCell,
      maxWidth: STUDENT_HEADER_WIDTH,
    },
    {
      Header: <HeaderWithHelp description={TIME_SPENT_DESCRIPTION} label="Time" />,
      ariaLabel: 'Time',
      accessor: SORT_BY_TIME_SPENT,
      resizable: false,
      maxWidth: 100,
      sortDescFirst: true,
      Cell: TimeSpentCell,
    },
    {
      Header: 'Activity',
      accessor: SORT_BY_ACTIVITY,
      resizable: false,
      Cell: ({ row }) => (row.original.onlineOnly ? NOT_IN_ACTIVITY : cell(original => safeParseHtml(original.activityName || REDACTED_ACTIVITY_NAME))({ row })),
    },
    // Fits 10 prompt dots and the count on one line
    { Header: 'Prompts completed', accessor: 'promptsCompleted', resizable: false, maxWidth: 216, disableSortBy: true, Cell: cell(row => <PromptCircles completed={row.completed} pending={row.progressPending} prompts={row.prompts} stage={row.stage} totalPrompts={row.totalPrompts} />) },
    { Header: 'May Need Help', accessor: 'mayNeedHelp', resizable: false, maxWidth: 230, disableSortBy: true, Cell: cell(row => <MayNeedHelpCell row={row} />) },
  ];
};

const rowId = (row: LiveViewRow) => row.activitySessionUid;

const ACTIVITY_COLUMN_INDEX = 2;
// Just below the line between the first and second rows
const CALL_OUT_TOP_OFFSET = 8;
const PROMPTS_COLUMN_INDEX = 3;
// Narrower than this, the call out sits below the table instead
export const MIN_CALL_OUT_OVERLAY_WIDTH = 768;

// rowsToCover: how many rows the table needs for the call out to sit within it
interface CallOutPosition { top: number, left: number, width: number, minHeight: number, rowsToCover: number }

// Without full access, the premium call out covers the blurred students: from
// the second row down, from the Activity column to halfway across Prompts
// completed. Measured after each
// render, since row heights and column widths follow the content. On narrow
// screens it sits below the table.
const useCallOutPosition = (enabled: boolean): [React.RefObject<HTMLDivElement>, React.RefObject<HTMLDivElement>, CallOutPosition|null] => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const callOutRef = React.useRef<HTMLDivElement>(null);
  const [position, setPosition] = React.useState<CallOutPosition|null>(null);
  const [, setResized] = React.useState(0);

  React.useEffect(() => {
    if (!enabled) { return undefined; }

    const onResize = () => setResized(count => count + 1);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [enabled]);

  React.useLayoutEffect(() => {
    const container = containerRef.current;
    const secondRow = container?.querySelectorAll('.rt-tbody .rt-tr')[1] as HTMLElement|undefined;
    const headers = container?.querySelectorAll('.rt-thead .rt-th');
    const activityHeader = headers?.[ACTIVITY_COLUMN_INDEX] as HTMLElement|undefined;
    const promptsHeader = headers?.[PROMPTS_COLUMN_INDEX] as HTMLElement|undefined;
    const fits = container?.clientWidth >= MIN_CALL_OUT_OVERLAY_WIDTH;
    let next: CallOutPosition|null = null;
    if (enabled && fits && secondRow && activityHeader && promptsHeader && callOutRef.current) {
      const origin = container.getBoundingClientRect();
      const top = secondRow.getBoundingClientRect().top - origin.top + CALL_OUT_TOP_OFFSET;
      const left = activityHeader.getBoundingClientRect().left - origin.left;
      const prompts = promptsHeader.getBoundingClientRect();
      const width = Math.round(prompts.left + prompts.width / 2 - origin.left - left);
      const callOutHeight = callOutRef.current.offsetHeight;
      const rowHeight = secondRow.getBoundingClientRect().height || 1;
      const rowsToCover = 1 + Math.ceil((callOutHeight + CALL_OUT_TOP_OFFSET) / rowHeight);
      next = { top, left, width, minHeight: top + callOutHeight, rowsToCover };
    }

    setPosition(current => (JSON.stringify(current) === JSON.stringify(next) ? current : next));
  });

  return [containerRef, callOutRef, position];
};

const LiveViewTable = ({ rows, fullAccess, sort = DEFAULT_SORT, onSortChange, maxRows, showcaseFirst, now = new Date(), emptyMessage = 'No students are currently online.', emptyAction, schoolAdmins, }: LiveViewTableProps) => {
  const showcase = fullAccess ? null : showcaseUid(rows);
  // Stable cell components: new ones would remount every cell on each live
  // update, dropping keyboard focus and closing any open help tooltip
  const tableColumns = React.useMemo(() => columns(fullAccess, showcase), [fullAccess, showcase]);
  const [containerRef, callOutRef, callOutPosition] = useCallOutPosition(!fullAccess);

  // With no rows to cover, the call out follows the empty state
  if (!rows.length) {
    return (
      <React.Fragment>
        <div className="live-view-empty-state">
          <p>{emptyMessage}</p>
          <p>This report updates automatically as students work.</p>
          {emptyAction}
        </div>
        {!fullAccess && <PremiumCallOut schoolAdmins={schoolAdmins} />}
      </React.Fragment>
    );
  }

  let orderedRows = sortRows(rows, sort, now);
  if (showcase && showcaseFirst) {
    orderedRows = [
      orderedRows.find(row => row.activitySessionUid === showcase),
      ...orderedRows.filter(row => row.activitySessionUid !== showcase),
    ];
  }
  let displayedRows: TableRow[] = maxRows ? orderedRows.slice(0, maxRows) : orderedRows;
  const neededRows = Math.max(MIN_PREVIEW_ROWS, callOutPosition?.rowsToCover || 0);
  const minRows = maxRows ? Math.min(neededRows, maxRows) : neededRows;
  if (!fullAccess && displayedRows.length < minRows) {
    displayedRows = [...displayedRows, ...placeholderRows(minRows - displayedRows.length)];
  }

  // The shared table types this as a string, but react-table passes its sortBy array
  const handleSortedChange = (sortBy: any) => {
    if (onSortChange && sortBy && sortBy[0]) { onSortChange(sortBy[0]); }
  };

  const overlaid = !!callOutPosition;

  return (
    <div className={`progress-reports-2018 live-view-table ${overlaid ? 'with-call-out' : ''}`} ref={containerRef} style={overlaid ? { minHeight: callOutPosition.minHeight } : undefined}>
      {/* react-table only tracks the header state; sortRows does the ordering.
          Rows are keyed by session so a reorder moves them rather than their content. */}
      <NowContext.Provider value={now}>
        <ReactTable
          className="progress-report"
          columns={tableColumns}
          data={displayedRows}
          defaultPageSize={SHOW_ALL_ROWS_PAGE_SIZE}
          defaultSorted={[sort]}
          disableSortBy={!onSortChange}
          disableSortRemove
          getRowId={rowId}
          getRowProps={placeholderRowProps}
          manualSortBy
          onSortedChange={handleSortedChange}
        />
      </NowContext.Provider>
      {!fullAccess && (
        <div
          className={`live-view-call-out-container ${overlaid ? 'overlaid' : ''}`}
          ref={callOutRef}
          style={overlaid ? { top: callOutPosition.top, left: callOutPosition.left, width: callOutPosition.width } : undefined}
        >
          <PremiumCallOut schoolAdmins={schoolAdmins} />
        </div>
      )}
    </div>
  );
};

export default LiveViewTable;
